import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { api } from "../../booking/api";
import { athensToday } from "../../booking/utils";
import { DayToolbar } from "./components/DayToolbar";

const euro = (n) => `€${Math.round(n).toLocaleString("en-GB")}`;

const Delta = ({ now, before }) => {
  if (now === before) return <span className="flex items-center gap-1 text-stone-400 text-xs"><Minus size={12} /> same as last week</span>;
  const up = now > before;
  const pct = before ? Math.round(((now - before) / before) * 100) : null;
  return (
    <span className={`flex items-center gap-1 text-xs ${up ? "text-emerald-600" : "text-red-600"}`}>
      {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {pct !== null ? `${up ? "+" : ""}${pct}%` : "new"} vs last week
    </span>
  );
};

const Card = ({ label, value, now, before, fmt = (x) => x, sub, testid }) => (
  <div className="bg-white border rounded-lg p-5" data-testid={testid}>
    <p className="text-xs uppercase tracking-[0.15em] text-stone-500">{label}</p>
    <p className="text-3xl font-semibold mt-2">{value ?? fmt(now)}</p>
    {sub && <p className="text-xs text-stone-500 mt-0.5">{sub}</p>}
    <div className="mt-2 flex items-center justify-between gap-2">
      <Delta now={now} before={before} /><span className="text-xs text-stone-400">Last week {fmt(before)}</span>
    </div>
  </div>
);

export default function RevenuePage() {
  const [date, setDate] = useState(athensToday());
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/api/admin/revenue", { params: { day: date } }).then((r) => setD(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const t = d?.today;
  const w = d?.last_week;
  const rev = (x) => x.massage_revenue + x.padel_revenue + x.deposits_paid;
  const maxOcc = Math.max(1, ...(d?.trend || []).map((x) => x.occupancy));
  return (
    <div data-testid="admin-revenue-page">
      <h1 className="text-2xl font-semibold mb-4">Daily Revenue Board</h1>
      <DayToolbar date={date} setDate={setDate} refreshing={loading} onRefresh={load} />
      {t && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            <Card testid="rev-occupancy" label="Occupancy" value={`${t.occupancy}%`} now={t.occupancy} before={w.occupancy}
              fmt={(x) => `${x}%`} sub={`${t.spots_used} of ${t.spots_total} spots`} />
            <Card testid="rev-bookings" label="Bookings" now={t.bookings} before={w.bookings} sub={`${t.guests} guests · ${t.walk_ins} walk-ins · ${t.cancelled} cancelled`} />
            <Card testid="rev-total" label="Bookable revenue" now={rev(t)} before={rev(w)} fmt={euro} sub="massages + padel + deposits" />
            <Card testid="rev-massages" label="Massages" now={t.massages} before={w.massages} sub={euro(t.massage_revenue)} />
            <Card testid="rev-padel" label="Padel" now={t.padel} before={w.padel} sub={euro(t.padel_revenue)} />
            <Card testid="rev-deposits" label="Deposits paid" now={t.deposits_paid} before={w.deposits_paid} fmt={euro} />
          </div>
          <div className="bg-white border rounded-lg p-5 mt-6" data-testid="rev-trend">
            <p className="text-xs uppercase tracking-[0.15em] text-stone-500 mb-4">Last 7 days · occupancy</p>
            <div className="flex items-end gap-3 h-40">
              {d.trend.map((x) => (
                <div key={x.date} className="flex-1 flex flex-col items-center gap-1 h-full justify-end" title={`${x.bookings} bookings · ${euro(x.revenue)}`}>
                  <span className="text-[11px] text-stone-500">{x.occupancy}%</span>
                  <div className={`w-full rounded-t ${x.date === date ? "bg-[#b09873]" : "bg-[#15262d]/80"} transition-[height] duration-500`}
                    style={{ height: `${Math.max(2, (x.occupancy / maxOcc) * 100)}%` }} />
                  <span className="text-[11px] text-stone-500">{x.date.slice(8)}/{x.date.slice(5, 7)}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-stone-400 mt-3">Lounges are free with minimum spend, so bar takings are not included. Prices come from Services.</p>
        </>
      )}
    </div>
  );
}
